/**
 * failureRehearsalPacks.js ✦ iter209
 * ─────────────────────────────────────────────────────────────
 * Canonical operational scenarios for release validation.
 *
 * Per Craig (iter209): "this is not feature creep. This is
 * operational ritual formalisation. Instead of 'try breaking it
 * somehow', you now get canonical operational stress scenarios."
 *
 * Each pack is a one-tap rehearsal setup:
 *   • camera + audio profile defaults
 *   • failure class + severity defaults
 *   • a reproductionSteps template the operator EDITS before
 *     capturing (Craig: "never make the pack fully automatic —
 *     human observational context preserves operational nuance")
 *   • expectedSignals — guidance for the operator on what
 *     telemetry SHOULD light up. NOT enforced. Teaches operators
 *     "what operational truth should look like".
 *
 * Packs are intentionally venue archetypes, not lab conditions.
 * Each one represents a real-world failure mode EventDrop has
 * to survive to be trustworthy under chaos.
 */
import { FAILURE_CLASSES } from './failureSignatures';

export const FAILURE_REHEARSAL_PACKS = Object.freeze({
  'dancefloor-darkness': Object.freeze({
    id: 'dancefloor-darkness',
    title: 'Dancefloor darkness',
    description: 'A guest tries to record a guestbook message in a dim venue lit only by ambient string lights or distant uplighters.',
    camera: 'guestbook',
    audio: 'voice_message',
    failureClass: FAILURE_CLASSES.LIGHT_UNDEREXPOSED,
    severity: 'major',
    reproductionSteps: 'Lights set to ambient only (no fill light). Subject seated 1m from camera, facing forward. Cycle started + 10s of speech.',
    expectedSignals: Object.freeze([
      'flags.underexposed=YES',
      'luminance.mean low (≤ 28)',
      'luminance.darkPct elevated',
    ]),
  }),

  'safari-background-resume': Object.freeze({
    id: 'safari-background-resume',
    title: 'Safari background → resume',
    description: 'Guest checks a notification mid-recording. Safari backgrounds the tab, then they swipe back.',
    camera: 'guestbook',
    audio: 'voice_message',
    failureClass: FAILURE_CLASSES.SAFARI_GC_COLLAPSE,
    severity: 'major',
    reproductionSteps: 'Started cycle. Backgrounded Safari for 90s via Home Indicator swipe. Returned to tab. Observed if camera + mic recovered.',
    expectedSignals: Object.freeze([
      'doc:hidden in timeline',
      'doc:visible in timeline',
      'cam:track-ended possible',
      'memory delta may spike',
    ]),
  }),

  'bluetooth-storm': Object.freeze({
    id: 'bluetooth-storm',
    title: 'Bluetooth storm',
    description: 'AirPods disconnect / reconnect repeatedly mid-recording — common at events with multiple paired devices nearby (DJ mixer, photographer\'s headphones, guests\' phones).',
    camera: 'guestbook',
    audio: 'voice_message',
    failureClass: FAILURE_CLASSES.BLUETOOTH_TRANSITION,
    severity: 'major',
    reproductionSteps: 'AirPods paired. Opened cycle. Removed one AirPod from ear briefly so connection cycled. Repeated twice.',
    expectedSignals: Object.freeze([
      'mic:devicechange ≥ 1',
      'mic:interrupt possible',
      'mic:track-ended possible',
      'audio rms gap during transition',
    ]),
  }),

  'venue-wifi-collapse': Object.freeze({
    id: 'venue-wifi-collapse',
    title: 'Venue Wi-Fi collapse',
    description: 'Hotspot or venue Wi-Fi drops during upload. Capture succeeds locally but the network round-trip fails.',
    camera: 'guestbook',
    audio: 'voice_message',
    failureClass: FAILURE_CLASSES.NETWORK_OFFLINE,
    severity: 'critical',
    reproductionSteps: 'Recorded a 5s cycle. Turned Wi-Fi off on the device during the upload window. Observed retry / failure path.',
    expectedSignals: Object.freeze([
      'conditions.onLine=false',
      'upload:fail counter increment',
    ]),
  }),

  'thermal-stress': Object.freeze({
    id: 'thermal-stress',
    title: 'Thermal stress',
    description: '20+ consecutive cycles on a warm device until iOS / Android thermal throttling kicks in. Common at outdoor venues in sunlight.',
    camera: 'guestbook',
    audio: 'voice_message',
    failureClass: FAILURE_CLASSES.THERMAL_THROTTLING,
    severity: 'major',
    reproductionSteps: 'Auto-loop set to 5s cycles. Ran 20+ cycles continuously. Logged ambient + chassis temperature subjectively.',
    expectedSignals: Object.freeze([
      'frame gap signature in cam:rec-* timeline',
      'memory delta growth',
      'cycle duration drift',
    ]),
  }),

  'backlit-window': Object.freeze({
    id: 'backlit-window',
    title: 'Backlit window',
    description: 'Subject seated with a bright window behind them — classic banquet hall framing. Front-cam metering crushes the highlights.',
    camera: 'portrait',
    audio: 'voice_message',
    failureClass: FAILURE_CLASSES.LIGHT_OVEREXPOSED,
    severity: 'minor',
    reproductionSteps: 'Subject positioned 1m from camera with window directly behind. Cycle started. Observed clipped pixels in export.',
    expectedSignals: Object.freeze([
      'flags.overexposed=YES',
      'luminance.clippedPct ≥ 18',
    ]),
  }),

  'mic-handover': Object.freeze({
    id: 'mic-handover',
    title: 'Mic handover',
    description: 'Wired headphones unplugged mid-recording (the host hands the phone to a guest who removes the cable).',
    camera: 'guestbook',
    audio: 'voice_message',
    failureClass: FAILURE_CLASSES.MIC_INTERRUPT,
    severity: 'major',
    reproductionSteps: 'Wired headphones connected. Cycle started + 4s. Cable physically unplugged. Recording continued or recovered.',
    expectedSignals: Object.freeze([
      'mic:track-ended ≥ 1',
      'mic:interrupt ≥ 1',
      'audioStatus may flip to idle',
    ]),
  }),

  'finger-on-lens': Object.freeze({
    id: 'finger-on-lens',
    title: 'Finger on lens',
    description: 'Guest accidentally covers the front camera with their thumb while holding the phone.',
    camera: 'guestbook',
    audio: 'voice_message',
    failureClass: FAILURE_CLASSES.LENS_OBSTRUCTED,
    severity: 'major',
    reproductionSteps: 'Cycle started. Index finger placed over front lens for ~4s mid-cycle, then removed.',
    expectedSignals: Object.freeze([
      'flags.lensObstructed=YES during obstruction',
      'luminance.variance very low',
      'luminance.mean mid-range (20..200)',
    ]),
  }),
});

export const FAILURE_REHEARSAL_PACK_LIST = Object.freeze(
  Object.values(FAILURE_REHEARSAL_PACKS),
);

export function getRehearsalPack(id) {
  const p = FAILURE_REHEARSAL_PACKS[id];
  if (!p) {
    const known = Object.keys(FAILURE_REHEARSAL_PACKS).join(', ');
    throw new Error(
      `[failureRehearsalPacks] unknown pack "${id}". Known packs: ${known}.`,
    );
  }
  return p;
}

export default {
  FAILURE_REHEARSAL_PACKS,
  FAILURE_REHEARSAL_PACK_LIST,
  getRehearsalPack,
};
